// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as Common from './common';
import * as Configuration from './configuration';

export function isLayoutEntryModel(source: any): source is ILayoutEntry {
    // Method Implementation: simple
    return source.modelName === 'LayoutEntry';
}

export function isSizingContextModel(source: any): source is ISizingContext {
    // Method Implementation: simple
    return source.modelName === 'SizingContext';
}

export function isSizingInitializationParamsModel(source: any): source is ISizingInitializationParams {
    // Method Implementation: simple
    return source.modelName === 'SizingInitializationParams';
}

export function isSizingSizeModel(source: any): source is ISizingSize {
    // Method Implementation: simple
    return source.modelName === 'SizingSize';
}

export function isSelectionContextModel(source: any): source is ISelectionContext {
    // Method Implementation: simple
    return source.modelName === 'SelectionContext';
}

export function isSelectionInitializationParamsModel(source: any): source is ISelectionInitializationParams {
    // Method Implementation: simple
    return source.modelName === 'SelectionInitializationParams';
}

export function isUpdateRequestModel(source: any): source is IUpdateRequest {
    // Method Implementation: simple
    return source.modelName === 'UpdateRequest';
}

export function isUpdateRequestSizeModel(source: any): source is IUpdateRequestSize {
    // Method Implementation: simple
    return source.modelName === 'UpdateRequestSize';
}

export interface LayoutEntryZodSchemaShape {
    layout?: string;

    subLayout?: string;

    grid?: string;
}

export interface SizingContextZodSchemaShape {
    entityKey?: string;

    layouts?: any;

    layoutName?: string;

    sizesGridName?: string;
}

export interface SizingInitializationParamsZodSchemaShape {
    application?: string;

    defaults?: Common.QuestionDefaultZodSchemaShape[];

    sizes?: SizingSizeZodSchemaShape[];
}

export interface SizingSizeZodSchemaShape {
    defaults?: Common.QuestionDefaultZodSchemaShape[];
}

export interface SelectionContextZodSchemaShape {
    entityKey?: string;

    sizesEntityKey?: string;

    layouts?: any;

    layoutName?: string;

    sizesGridName?: string;
}

export interface SelectionInitializationParamsZodSchemaShape {
    application?: string;

    entityKey?: string;

    defaults?: Common.QuestionDefaultZodSchemaShape[];
}

export interface UpdateRequestZodSchemaShape {
    questions?: Configuration.EntityStateQuestionZodSchemaShape[];

    addedSizes?: UpdateRequestSizeZodSchemaShape[];

    removedSizes?: string[];
}

export interface UpdateRequestSizeZodSchemaShape {
    defaults?: Common.QuestionDefaultZodSchemaShape[];
}

// Artifact Implementation: artifacts/zodSchemaType
export const LayoutEntryZodSchema: Zod.ZodSchema<LayoutEntryZodSchemaShape> = Zod.lazy(() => Zod.object({
    layout: Zod.string().optional(),
    subLayout: Zod.string().optional(),
    grid: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const SizingContextZodSchema: Zod.ZodSchema<SizingContextZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityKey: Zod.string().optional(),
    layouts: Zod.any().optional(),
    layoutName: Zod.string().optional(),
    sizesGridName: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const SizingInitializationParamsZodSchema: Zod.ZodSchema<SizingInitializationParamsZodSchemaShape> = Zod.lazy(() => Zod.object({
    application: Zod.string().optional(),
    defaults: Zod.array(Common.QuestionDefaultZodSchema).optional(),
    sizes: Zod.array(SizingSizeZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const SizingSizeZodSchema: Zod.ZodSchema<SizingSizeZodSchemaShape> = Zod.lazy(() => Zod.object({
    defaults: Zod.array(Common.QuestionDefaultZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const SelectionContextZodSchema: Zod.ZodSchema<SelectionContextZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityKey: Zod.string().optional(),
    sizesEntityKey: Zod.string().optional(),
    layouts: Zod.any().optional(),
    layoutName: Zod.string().optional(),
    sizesGridName: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const SelectionInitializationParamsZodSchema: Zod.ZodSchema<SelectionInitializationParamsZodSchemaShape> = Zod.lazy(() => Zod.object({
    application: Zod.string().optional(),
    entityKey: Zod.string().optional(),
    defaults: Zod.array(Common.QuestionDefaultZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const UpdateRequestZodSchema: Zod.ZodSchema<UpdateRequestZodSchemaShape> = Zod.lazy(() => Zod.object({
    questions: Zod.array(Configuration.EntityStateQuestionZodSchema).optional(),
    addedSizes: Zod.array(UpdateRequestSizeZodSchema).optional(),
    removedSizes: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const UpdateRequestSizeZodSchema: Zod.ZodSchema<UpdateRequestSizeZodSchemaShape> = Zod.lazy(() => Zod.object({
    defaults: Zod.array(Common.QuestionDefaultZodSchema).optional()
}).nonstrict());

export interface ILayoutEntry extends Wellspring.IModel {
    readonly modelName: string;

    readonly layout: string;

    readonly subLayout: string;

    readonly grid: string;

    readonly isLayoutDefined: boolean;

    readonly isSubLayoutDefined: boolean;

    readonly isGridDefined: boolean;
}

export interface ISizingContext extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityKey: string;

    readonly layouts: Record<string, LayoutEntry>;

    readonly layoutName: string;

    readonly sizesGridName: string;

    readonly isEntityKeyDefined: boolean;

    readonly isLayoutsDefined: boolean;

    readonly isLayoutNameDefined: boolean;

    readonly isSizesGridNameDefined: boolean;
}

export interface ISizingInitializationParams extends Wellspring.IModel {
    readonly modelName: string;

    readonly application: string;

    readonly defaults: Common.QuestionDefault[];

    readonly sizes: SizingSize[];

    readonly isApplicationDefined: boolean;

    readonly isDefaultsDefined: boolean;

    readonly isSizesDefined: boolean;
}

export interface ISizingSize extends Wellspring.IModel {
    readonly modelName: string;

    readonly defaults: Common.QuestionDefault[];

    readonly isDefaultsDefined: boolean;
}

export interface ISelectionContext extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityKey: string;

    readonly sizesEntityKey: string;

    readonly layouts: Record<string, LayoutEntry>;

    readonly layoutName: string;

    readonly sizesGridName: string;

    readonly isEntityKeyDefined: boolean;

    readonly isSizesEntityKeyDefined: boolean;

    readonly isLayoutsDefined: boolean;

    readonly isLayoutNameDefined: boolean;

    readonly isSizesGridNameDefined: boolean;
}

export interface ISelectionInitializationParams extends Wellspring.IModel {
    readonly modelName: string;

    readonly application: string;

    readonly entityKey: string;

    readonly defaults: Common.QuestionDefault[];

    readonly isApplicationDefined: boolean;

    readonly isEntityKeyDefined: boolean;

    readonly isDefaultsDefined: boolean;
}

export interface IUpdateRequest extends Wellspring.IModel {
    readonly modelName: string;

    readonly questions: Configuration.EntityStateQuestion[];

    readonly addedSizes: UpdateRequestSize[];

    readonly removedSizes: string[];

    readonly isQuestionsDefined: boolean;

    readonly isAddedSizesDefined: boolean;

    readonly isRemovedSizesDefined: boolean;
}

export interface IUpdateRequestSize extends Wellspring.IModel {
    readonly modelName: string;

    readonly defaults: Common.QuestionDefault[];

    readonly isDefaultsDefined: boolean;
}

export class LayoutEntry implements ILayoutEntry {
    public static readonly modelName: string = 'LayoutEntry';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'layout', 'subLayout', 'grid' ];

    public get modelName(): string {
        // Method Implementation: simple
        return LayoutEntry.modelName;
    }

    private _layout: string;

    private _subLayout: string;

    private _grid: string;

    public get layout(): string {
        // Method Implementation: simple
        return this._layout;
    }

    public get subLayout(): string {
        // Method Implementation: simple
        return this._subLayout;
    }

    public get grid(): string {
        // Method Implementation: simple
        return this._grid;
    }

    public set layout(value: string) {
        // Method Implementation: simple
        this._layout = value;
        this._isLayoutDefined = true;
    }

    public set subLayout(value: string) {
        // Method Implementation: simple
        this._subLayout = value;
        this._isSubLayoutDefined = true;
    }

    public set grid(value: string) {
        // Method Implementation: simple
        this._grid = value;
        this._isGridDefined = true;
    }

    private _isLayoutDefined: boolean;

    private _isSubLayoutDefined: boolean;

    private _isGridDefined: boolean;

    public get isLayoutDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutDefined;
    }

    public get isSubLayoutDefined(): boolean {
        // Method Implementation: simple
        return this._isSubLayoutDefined;
    }

    public get isGridDefined(): boolean {
        // Method Implementation: simple
        return this._isGridDefined;
    }

    public constructor();
    public constructor(source: ILayoutEntry);
    public constructor(source: Partial<Pick<LayoutEntry, 'layout' | 'subLayout' | 'grid'>>);
    public constructor(source?: ILayoutEntry | Partial<Pick<LayoutEntry, 'layout' | 'subLayout' | 'grid'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLayoutEntryModel(source)) {
                if (source.isLayoutDefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = '';
                    this._isLayoutDefined = false;
                }
                
                if (source.isSubLayoutDefined) {
                    this._subLayout = source.subLayout;
                    this._isSubLayoutDefined = true;
                }
                else {
                    this._subLayout = '';
                    this._isSubLayoutDefined = false;
                }
                
                if (source.isGridDefined) {
                    this._grid = source.grid;
                    this._isGridDefined = true;
                }
                else {
                    this._grid = '';
                    this._isGridDefined = false;
                }
            }
            else {
                if (source.layout !== undefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = '';
                    this._isLayoutDefined = false;
                }
                
                if (source.subLayout !== undefined) {
                    this._subLayout = source.subLayout;
                    this._isSubLayoutDefined = true;
                }
                else {
                    this._subLayout = '';
                    this._isSubLayoutDefined = false;
                }
                
                if (source.grid !== undefined) {
                    this._grid = source.grid;
                    this._isGridDefined = true;
                }
                else {
                    this._grid = '';
                    this._isGridDefined = false;
                }
            }
        }
        else {
            this._layout = '';
            this._isLayoutDefined = false;
        
            this._subLayout = '';
            this._isSubLayoutDefined = false;
        
            this._grid = '';
            this._isGridDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return LayoutEntry.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isLayoutDefined && this.layout !== undefined)
            definedProperties.push('layout');
        if (this.isSubLayoutDefined && this.subLayout !== undefined)
            definedProperties.push('subLayout');
        if (this.isGridDefined && this.grid !== undefined)
            definedProperties.push('grid');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'layout':
                return this.layout;
            case 'subLayout':
                return this.subLayout;
            case 'grid':
                return this.grid;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isLayoutDefined && this.layout !== undefined) {
            if (this.layout !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.layout);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'null'));
            }
        }
        
        if (this.isSubLayoutDefined && this.subLayout !== undefined) {
            if (this.subLayout !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('subLayout', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.subLayout);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('subLayout', 'null'));
            }
        }
        
        if (this.isGridDefined && this.grid !== undefined) {
            if (this.grid !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('grid', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.grid);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('grid', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'layout':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.layout = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'layout' cannot be null`);
                    this.layout = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layout'`);
                }
        
                return true;
            case 'subLayout':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.subLayout = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'subLayout' cannot be null`);
                    this.subLayout = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'subLayout'`);
                }
        
                return true;
            case 'grid':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.grid = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'grid' cannot be null`);
                    this.grid = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'grid'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // layout
        if (this.isLayoutDefined && this.layout !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.layout === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.layout);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // subLayout
        if (this.isSubLayoutDefined && this.subLayout !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.subLayout === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.subLayout);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // grid
        if (this.isGridDefined && this.grid !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.grid === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.grid);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // layout
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.layout = <any>null;
                    break;
                case 1:
                    this.layout = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // subLayout
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.subLayout = <any>null;
                    break;
                case 1:
                    this.subLayout = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // grid
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.grid = <any>null;
                    break;
                case 1:
                    this.grid = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): LayoutEntry {
        // Method Implementation: simple
        const schema = LayoutEntryZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LayoutEntryZodSchemaShape): LayoutEntry {
        // Method Implementation: parseZodSchema
        return new LayoutEntry({
            layout: schema.layout,
            subLayout: schema.subLayout,
            grid: schema.grid
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            layout: this.isLayoutDefined ? ((this.layout === null ? undefined : this.layout)) : undefined,
            subLayout: this.isSubLayoutDefined ? ((this.subLayout === null ? undefined : this.subLayout)) : undefined,
            grid: this.isGridDefined ? ((this.grid === null ? undefined : this.grid)) : undefined,
        };
    }

    public clone(): LayoutEntry {
        // Method Implementation: clone
        return new LayoutEntry({
            layout: this.isLayoutDefined ? this.layout : undefined,
            subLayout: this.isSubLayoutDefined ? this.subLayout : undefined,
            grid: this.isGridDefined ? this.grid : undefined
        });
    }
}

export class SizingContext implements ISizingContext {
    public static readonly modelName: string = 'SizingContext';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityKey', 'layouts', 'layoutName', 'sizesGridName' ];

    public get modelName(): string {
        // Method Implementation: simple
        return SizingContext.modelName;
    }

    private _entityKey: string;

    private _layouts: Record<string, LayoutEntry>;

    private _layoutName: string;

    private _sizesGridName: string;

    public get entityKey(): string {
        // Method Implementation: simple
        return this._entityKey;
    }

    public get layouts(): Record<string, LayoutEntry> {
        // Method Implementation: simple
        return this._layouts;
    }

    public get layoutName(): string {
        // Method Implementation: simple
        return this._layoutName;
    }

    public get sizesGridName(): string {
        // Method Implementation: simple
        return this._sizesGridName;
    }

    public set entityKey(value: string) {
        // Method Implementation: simple
        this._entityKey = value;
        this._isEntityKeyDefined = true;
    }

    public set layouts(value: Record<string, LayoutEntry>) {
        // Method Implementation: simple
        this._layouts = value;
        this._isLayoutsDefined = true;
    }

    public set layoutName(value: string) {
        // Method Implementation: simple
        this._layoutName = value;
        this._isLayoutNameDefined = true;
    }

    public set sizesGridName(value: string) {
        // Method Implementation: simple
        this._sizesGridName = value;
        this._isSizesGridNameDefined = true;
    }

    private _isEntityKeyDefined: boolean;

    private _isLayoutsDefined: boolean;

    private _isLayoutNameDefined: boolean;

    private _isSizesGridNameDefined: boolean;

    public get isEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityKeyDefined;
    }

    public get isLayoutsDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutsDefined;
    }

    public get isLayoutNameDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutNameDefined;
    }

    public get isSizesGridNameDefined(): boolean {
        // Method Implementation: simple
        return this._isSizesGridNameDefined;
    }

    public constructor();
    public constructor(source: ISizingContext);
    public constructor(source: Partial<Pick<SizingContext, 'entityKey' | 'layouts' | 'layoutName' | 'sizesGridName'>>);
    public constructor(source?: ISizingContext | Partial<Pick<SizingContext, 'entityKey' | 'layouts' | 'layoutName' | 'sizesGridName'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSizingContextModel(source)) {
                if (source.isEntityKeyDefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.isLayoutsDefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = {};
                    this._isLayoutsDefined = true;
                }
                
                if (source.isLayoutNameDefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
                
                if (source.isSizesGridNameDefined) {
                    this._sizesGridName = source.sizesGridName;
                    this._isSizesGridNameDefined = true;
                }
                else {
                    this._sizesGridName = '';
                    this._isSizesGridNameDefined = false;
                }
            }
            else {
                if (source.entityKey !== undefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.layouts !== undefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = {};
                    this._isLayoutsDefined = true;
                }
                
                if (source.layoutName !== undefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
                
                if (source.sizesGridName !== undefined) {
                    this._sizesGridName = source.sizesGridName;
                    this._isSizesGridNameDefined = true;
                }
                else {
                    this._sizesGridName = '';
                    this._isSizesGridNameDefined = false;
                }
            }
        }
        else {
            this._entityKey = '';
            this._isEntityKeyDefined = false;
        
            this._layouts = {};
            this._isLayoutsDefined = true;
        
            this._layoutName = '';
            this._isLayoutNameDefined = false;
        
            this._sizesGridName = '';
            this._isSizesGridNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return SizingContext.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityKeyDefined && this.entityKey !== undefined)
            definedProperties.push('entityKey');
        if (this.isLayoutsDefined && this.layouts !== undefined)
            definedProperties.push('layouts');
        if (this.isLayoutNameDefined && this.layoutName !== undefined)
            definedProperties.push('layoutName');
        if (this.isSizesGridNameDefined && this.sizesGridName !== undefined)
            definedProperties.push('sizesGridName');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityKey':
                return this.entityKey;
            case 'layouts':
                return this.layouts;
            case 'layoutName':
                return this.layoutName;
            case 'sizesGridName':
                return this.sizesGridName;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            if (this.entityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'null'));
            }
        }
        
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            if (this.layouts !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'dictionary'));
        
                // Implementation: axon/write/dictionary
                writer.writeDictionaryHeader(new Axon.DictionaryHeader(_.keys(this.layouts).length));
                for (let key in this.layouts) {
                    const dictionaryValue = this.layouts[key];
                
                    writer.writeDictionaryItemHeader(new Axon.DictionaryItemHeader('string', dictionaryValue !== null ? 'model' : 'null'));
                
                    // Key Implementation: axon/write/string
                    writer.writeStringValue(key);
                
                    if (dictionaryValue !== null) {
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(dictionaryValue.getModelName(), dictionaryValue.getDefinedPropertyNames().length));
                        dictionaryValue.write(writer);
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'null'));
            }
        }
        
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            if (this.layoutName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.layoutName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'null'));
            }
        }
        
        if (this.isSizesGridNameDefined && this.sizesGridName !== undefined) {
            if (this.sizesGridName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizesGridName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.sizesGridName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizesGridName', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityKey' cannot be null`);
                    this.entityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityKey'`);
                }
        
                return true;
            case 'layouts':
                // Implementation: axon/readProperty/dictionary
                if (propertyHeader.type === 'dictionary') {
                    const dictionaryHeader = reader.readDictionaryHeader();
                
                    this.layouts = {};
                
                    for (let i = 0; i < dictionaryHeader.recordCount; i++) {
                        let key: string = <any>undefined;
                        let value: LayoutEntry = <any>undefined;
                
                        const dictionaryItemHeader = reader.readDictionaryItemHeader();
                
                        // Key Implementation: string
                        if (dictionaryItemHeader.keyType === 'string') {
                            key = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.keyType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'layouts' cannot be null`);
                            key = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.keyType}' for property 'layouts'`);
                        }
                
                        // Value Implementation: model
                        if (dictionaryItemHeader.valueType === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === LayoutEntry.modelName) {
                                value = new LayoutEntry();
                                value.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${LayoutEntry.modelName}'`);
                            }
                        }
                        else if (dictionaryItemHeader.valueType === 'null') {
                            throw new Error(`Value for property 'layouts' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.valueType}' for property 'layouts'`);
                        }
                
                        this.layouts[key] = value;
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'layouts' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layouts'`);
                }
        
                return true;
            case 'layoutName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.layoutName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'layoutName' cannot be null`);
                    this.layoutName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layoutName'`);
                }
        
                return true;
            case 'sizesGridName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.sizesGridName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'sizesGridName' cannot be null`);
                    this.sizesGridName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sizesGridName'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityKey
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layouts
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/dictionary
            if (this.layouts === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(_.keys(this.layouts).length);
            
                for (let key in this.layouts) {
                    // Key Implementation: string
                    if (key === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(key);
                    }
            
                    // Value Implementation: model
                    if (this.layouts[key] === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        this.layouts[key].slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layoutName
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.layoutName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.layoutName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sizesGridName
        if (this.isSizesGridNameDefined && this.sizesGridName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.sizesGridName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.sizesGridName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityKey = <any>null;
                    break;
                case 1:
                    this.entityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layouts
        if (reader.readBooleanValue())
        {
            // Implementation: dictionary
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null dictionaries');
                    break;
                case 1:
                    let key: string;
                    let value: LayoutEntry;
            
                    this.layouts = {};
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Key Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                key = <any>null;
                                break;
                            case 1:
                                key = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        // Value Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                value = new LayoutEntry();
                                value.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.layouts[key] = value;
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layoutName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.layoutName = <any>null;
                    break;
                case 1:
                    this.layoutName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // sizesGridName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.sizesGridName = <any>null;
                    break;
                case 1:
                    this.sizesGridName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): SizingContext {
        // Method Implementation: simple
        const schema = SizingContextZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SizingContextZodSchemaShape): SizingContext {
        // Method Implementation: parseZodSchema
        return new SizingContext({
            entityKey: schema.entityKey,
            layouts: schema.layouts,
            layoutName: schema.layoutName,
            sizesGridName: schema.sizesGridName
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityKey: this.isEntityKeyDefined ? ((this.entityKey === null ? undefined : this.entityKey)) : undefined,
            layouts: this.isLayoutsDefined ? (this.layouts) : undefined,
            layoutName: this.isLayoutNameDefined ? ((this.layoutName === null ? undefined : this.layoutName)) : undefined,
            sizesGridName: this.isSizesGridNameDefined ? ((this.sizesGridName === null ? undefined : this.sizesGridName)) : undefined,
        };
    }

    public clone(): SizingContext {
        // Method Implementation: clone
        return new SizingContext({
            entityKey: this.isEntityKeyDefined ? this.entityKey : undefined,
            layouts: this.isLayoutsDefined ? this.layouts : undefined,
            layoutName: this.isLayoutNameDefined ? this.layoutName : undefined,
            sizesGridName: this.isSizesGridNameDefined ? this.sizesGridName : undefined
        });
    }
}

export class SizingInitializationParams implements ISizingInitializationParams {
    public static readonly modelName: string = 'SizingInitializationParams';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'application', 'defaults', 'sizes' ];

    public get modelName(): string {
        // Method Implementation: simple
        return SizingInitializationParams.modelName;
    }

    private _application: string;

    private _defaults: Common.QuestionDefault[];

    private _sizes: SizingSize[];

    public get application(): string {
        // Method Implementation: simple
        return this._application;
    }

    public get defaults(): Common.QuestionDefault[] {
        // Method Implementation: simple
        return this._defaults;
    }

    public get sizes(): SizingSize[] {
        // Method Implementation: simple
        return this._sizes;
    }

    public set application(value: string) {
        // Method Implementation: simple
        this._application = value;
        this._isApplicationDefined = true;
    }

    public set defaults(value: Common.QuestionDefault[]) {
        // Method Implementation: simple
        this._defaults = value;
        this._isDefaultsDefined = true;
    }

    public set sizes(value: SizingSize[]) {
        // Method Implementation: simple
        this._sizes = value;
        this._isSizesDefined = true;
    }

    private _isApplicationDefined: boolean;

    private _isDefaultsDefined: boolean;

    private _isSizesDefined: boolean;

    public get isApplicationDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicationDefined;
    }

    public get isDefaultsDefined(): boolean {
        // Method Implementation: simple
        return this._isDefaultsDefined;
    }

    public get isSizesDefined(): boolean {
        // Method Implementation: simple
        return this._isSizesDefined;
    }

    public constructor();
    public constructor(source: ISizingInitializationParams);
    public constructor(source: Partial<Pick<SizingInitializationParams, 'application' | 'defaults' | 'sizes'>>);
    public constructor(source?: ISizingInitializationParams | Partial<Pick<SizingInitializationParams, 'application' | 'defaults' | 'sizes'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSizingInitializationParamsModel(source)) {
                if (source.isApplicationDefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.isDefaultsDefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
                
                if (source.isSizesDefined) {
                    this._sizes = source.sizes;
                    this._isSizesDefined = true;
                }
                else {
                    this._sizes = [];
                    this._isSizesDefined = true;
                }
            }
            else {
                if (source.application !== undefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.defaults !== undefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
                
                if (source.sizes !== undefined) {
                    this._sizes = source.sizes;
                    this._isSizesDefined = true;
                }
                else {
                    this._sizes = [];
                    this._isSizesDefined = true;
                }
            }
        }
        else {
            this._application = '';
            this._isApplicationDefined = false;
        
            this._defaults = [];
            this._isDefaultsDefined = true;
        
            this._sizes = [];
            this._isSizesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return SizingInitializationParams.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isApplicationDefined && this.application !== undefined)
            definedProperties.push('application');
        if (this.isDefaultsDefined && this.defaults !== undefined)
            definedProperties.push('defaults');
        if (this.isSizesDefined && this.sizes !== undefined)
            definedProperties.push('sizes');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'application':
                return this.application;
            case 'defaults':
                return this.defaults;
            case 'sizes':
                return this.sizes;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isApplicationDefined && this.application !== undefined) {
            if (this.application !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.application);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'null'));
            }
        }
        
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            if (this.defaults !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.defaults.length));
                for (let i = 0; i < this.defaults.length; i++) {
                    const item = this.defaults[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'null'));
            }
        }
        
        if (this.isSizesDefined && this.sizes !== undefined) {
            if (this.sizes !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizes', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.sizes.length));
                for (let i = 0; i < this.sizes.length; i++) {
                    const item = this.sizes[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizes', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'application':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.application = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'application' cannot be null`);
                    this.application = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'application'`);
                }
        
                return true;
            case 'defaults':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.defaults = [];
                
                    let item: Common.QuestionDefault = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Common.QuestionDefault.modelName) {
                                item = new Common.QuestionDefault();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Common.QuestionDefault.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'defaults' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'defaults'`);
                        }
                
                        this.defaults.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'defaults' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'defaults'`);
                }
        
                return true;
            case 'sizes':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.sizes = [];
                
                    let item: SizingSize = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === SizingSize.modelName) {
                                item = new SizingSize();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${SizingSize.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'sizes' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'sizes'`);
                        }
                
                        this.sizes.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'sizes' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sizes'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // application
        if (this.isApplicationDefined && this.application !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.application === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.application);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // defaults
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.defaults === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.defaults.length);
            
                for (const item of this.defaults) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sizes
        if (this.isSizesDefined && this.sizes !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.sizes === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.sizes.length);
            
                for (const item of this.sizes) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // application
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.application = <any>null;
                    break;
                case 1:
                    this.application = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // defaults
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Common.QuestionDefault;
            
                    this.defaults = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Common.QuestionDefault();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.defaults.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // sizes
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: SizingSize;
            
                    this.sizes = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new SizingSize();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.sizes.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): SizingInitializationParams {
        // Method Implementation: simple
        const schema = SizingInitializationParamsZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SizingInitializationParamsZodSchemaShape): SizingInitializationParams {
        // Method Implementation: parseZodSchema
        return new SizingInitializationParams({
            application: schema.application,
            defaults: schema.defaults?.map(i => Common.QuestionDefault.parseZodSchema(i)),
            sizes: schema.sizes?.map(i => SizingSize.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            application: this.isApplicationDefined ? ((this.application === null ? undefined : this.application)) : undefined,
            defaults: this.isDefaultsDefined ? (this.defaults?.map(item => item)) : undefined,
            sizes: this.isSizesDefined ? (this.sizes?.map(item => item)) : undefined,
        };
    }

    public clone(): SizingInitializationParams {
        // Method Implementation: clone
        return new SizingInitializationParams({
            application: this.isApplicationDefined ? this.application : undefined,
            defaults: this.isDefaultsDefined ? this.defaults?.map(i => i.clone()) : undefined,
            sizes: this.isSizesDefined ? this.sizes?.map(i => i.clone()) : undefined
        });
    }
}

export class SizingSize implements ISizingSize {
    public static readonly modelName: string = 'SizingSize';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'defaults' ];

    public get modelName(): string {
        // Method Implementation: simple
        return SizingSize.modelName;
    }

    private _defaults: Common.QuestionDefault[];

    public get defaults(): Common.QuestionDefault[] {
        // Method Implementation: simple
        return this._defaults;
    }

    public set defaults(value: Common.QuestionDefault[]) {
        // Method Implementation: simple
        this._defaults = value;
        this._isDefaultsDefined = true;
    }

    private _isDefaultsDefined: boolean;

    public get isDefaultsDefined(): boolean {
        // Method Implementation: simple
        return this._isDefaultsDefined;
    }

    public constructor();
    public constructor(source: ISizingSize);
    public constructor(source: Partial<Pick<SizingSize, 'defaults'>>);
    public constructor(source?: ISizingSize | Partial<Pick<SizingSize, 'defaults'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSizingSizeModel(source)) {
                if (source.isDefaultsDefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
            else {
                if (source.defaults !== undefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
        }
        else {
            this._defaults = [];
            this._isDefaultsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return SizingSize.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isDefaultsDefined && this.defaults !== undefined)
            definedProperties.push('defaults');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'defaults':
                return this.defaults;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            if (this.defaults !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.defaults.length));
                for (let i = 0; i < this.defaults.length; i++) {
                    const item = this.defaults[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'defaults':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.defaults = [];
                
                    let item: Common.QuestionDefault = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Common.QuestionDefault.modelName) {
                                item = new Common.QuestionDefault();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Common.QuestionDefault.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'defaults' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'defaults'`);
                        }
                
                        this.defaults.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'defaults' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'defaults'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // defaults
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.defaults === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.defaults.length);
            
                for (const item of this.defaults) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // defaults
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Common.QuestionDefault;
            
                    this.defaults = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Common.QuestionDefault();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.defaults.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): SizingSize {
        // Method Implementation: simple
        const schema = SizingSizeZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SizingSizeZodSchemaShape): SizingSize {
        // Method Implementation: parseZodSchema
        return new SizingSize({
            defaults: schema.defaults?.map(i => Common.QuestionDefault.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            defaults: this.isDefaultsDefined ? (this.defaults?.map(item => item)) : undefined,
        };
    }

    public clone(): SizingSize {
        // Method Implementation: clone
        return new SizingSize({
            defaults: this.isDefaultsDefined ? this.defaults?.map(i => i.clone()) : undefined
        });
    }
}

export class SelectionContext implements ISelectionContext {
    public static readonly modelName: string = 'SelectionContext';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityKey', 'sizesEntityKey', 'layouts', 'layoutName', 'sizesGridName' ];

    public get modelName(): string {
        // Method Implementation: simple
        return SelectionContext.modelName;
    }

    private _entityKey: string;

    private _sizesEntityKey: string;

    private _layouts: Record<string, LayoutEntry>;

    private _layoutName: string;

    private _sizesGridName: string;

    public get entityKey(): string {
        // Method Implementation: simple
        return this._entityKey;
    }

    public get sizesEntityKey(): string {
        // Method Implementation: simple
        return this._sizesEntityKey;
    }

    public get layouts(): Record<string, LayoutEntry> {
        // Method Implementation: simple
        return this._layouts;
    }

    public get layoutName(): string {
        // Method Implementation: simple
        return this._layoutName;
    }

    public get sizesGridName(): string {
        // Method Implementation: simple
        return this._sizesGridName;
    }

    public set entityKey(value: string) {
        // Method Implementation: simple
        this._entityKey = value;
        this._isEntityKeyDefined = true;
    }

    public set sizesEntityKey(value: string) {
        // Method Implementation: simple
        this._sizesEntityKey = value;
        this._isSizesEntityKeyDefined = true;
    }

    public set layouts(value: Record<string, LayoutEntry>) {
        // Method Implementation: simple
        this._layouts = value;
        this._isLayoutsDefined = true;
    }

    public set layoutName(value: string) {
        // Method Implementation: simple
        this._layoutName = value;
        this._isLayoutNameDefined = true;
    }

    public set sizesGridName(value: string) {
        // Method Implementation: simple
        this._sizesGridName = value;
        this._isSizesGridNameDefined = true;
    }

    private _isEntityKeyDefined: boolean;

    private _isSizesEntityKeyDefined: boolean;

    private _isLayoutsDefined: boolean;

    private _isLayoutNameDefined: boolean;

    private _isSizesGridNameDefined: boolean;

    public get isEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityKeyDefined;
    }

    public get isSizesEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isSizesEntityKeyDefined;
    }

    public get isLayoutsDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutsDefined;
    }

    public get isLayoutNameDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutNameDefined;
    }

    public get isSizesGridNameDefined(): boolean {
        // Method Implementation: simple
        return this._isSizesGridNameDefined;
    }

    public constructor();
    public constructor(source: ISelectionContext);
    public constructor(source: Partial<Pick<SelectionContext, 'entityKey' | 'sizesEntityKey' | 'layouts' | 'layoutName' | 'sizesGridName'>>);
    public constructor(source?: ISelectionContext | Partial<Pick<SelectionContext, 'entityKey' | 'sizesEntityKey' | 'layouts' | 'layoutName' | 'sizesGridName'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSelectionContextModel(source)) {
                if (source.isEntityKeyDefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.isSizesEntityKeyDefined) {
                    this._sizesEntityKey = source.sizesEntityKey;
                    this._isSizesEntityKeyDefined = true;
                }
                else {
                    this._sizesEntityKey = '';
                    this._isSizesEntityKeyDefined = false;
                }
                
                if (source.isLayoutsDefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = {};
                    this._isLayoutsDefined = true;
                }
                
                if (source.isLayoutNameDefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
                
                if (source.isSizesGridNameDefined) {
                    this._sizesGridName = source.sizesGridName;
                    this._isSizesGridNameDefined = true;
                }
                else {
                    this._sizesGridName = '';
                    this._isSizesGridNameDefined = false;
                }
            }
            else {
                if (source.entityKey !== undefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.sizesEntityKey !== undefined) {
                    this._sizesEntityKey = source.sizesEntityKey;
                    this._isSizesEntityKeyDefined = true;
                }
                else {
                    this._sizesEntityKey = '';
                    this._isSizesEntityKeyDefined = false;
                }
                
                if (source.layouts !== undefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = {};
                    this._isLayoutsDefined = true;
                }
                
                if (source.layoutName !== undefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
                
                if (source.sizesGridName !== undefined) {
                    this._sizesGridName = source.sizesGridName;
                    this._isSizesGridNameDefined = true;
                }
                else {
                    this._sizesGridName = '';
                    this._isSizesGridNameDefined = false;
                }
            }
        }
        else {
            this._entityKey = '';
            this._isEntityKeyDefined = false;
        
            this._sizesEntityKey = '';
            this._isSizesEntityKeyDefined = false;
        
            this._layouts = {};
            this._isLayoutsDefined = true;
        
            this._layoutName = '';
            this._isLayoutNameDefined = false;
        
            this._sizesGridName = '';
            this._isSizesGridNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return SelectionContext.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityKeyDefined && this.entityKey !== undefined)
            definedProperties.push('entityKey');
        if (this.isSizesEntityKeyDefined && this.sizesEntityKey !== undefined)
            definedProperties.push('sizesEntityKey');
        if (this.isLayoutsDefined && this.layouts !== undefined)
            definedProperties.push('layouts');
        if (this.isLayoutNameDefined && this.layoutName !== undefined)
            definedProperties.push('layoutName');
        if (this.isSizesGridNameDefined && this.sizesGridName !== undefined)
            definedProperties.push('sizesGridName');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityKey':
                return this.entityKey;
            case 'sizesEntityKey':
                return this.sizesEntityKey;
            case 'layouts':
                return this.layouts;
            case 'layoutName':
                return this.layoutName;
            case 'sizesGridName':
                return this.sizesGridName;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            if (this.entityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'null'));
            }
        }
        
        if (this.isSizesEntityKeyDefined && this.sizesEntityKey !== undefined) {
            if (this.sizesEntityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizesEntityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.sizesEntityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizesEntityKey', 'null'));
            }
        }
        
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            if (this.layouts !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'dictionary'));
        
                // Implementation: axon/write/dictionary
                writer.writeDictionaryHeader(new Axon.DictionaryHeader(_.keys(this.layouts).length));
                for (let key in this.layouts) {
                    const dictionaryValue = this.layouts[key];
                
                    writer.writeDictionaryItemHeader(new Axon.DictionaryItemHeader('string', dictionaryValue !== null ? 'model' : 'null'));
                
                    // Key Implementation: axon/write/string
                    writer.writeStringValue(key);
                
                    if (dictionaryValue !== null) {
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(dictionaryValue.getModelName(), dictionaryValue.getDefinedPropertyNames().length));
                        dictionaryValue.write(writer);
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'null'));
            }
        }
        
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            if (this.layoutName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.layoutName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'null'));
            }
        }
        
        if (this.isSizesGridNameDefined && this.sizesGridName !== undefined) {
            if (this.sizesGridName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizesGridName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.sizesGridName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizesGridName', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityKey' cannot be null`);
                    this.entityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityKey'`);
                }
        
                return true;
            case 'sizesEntityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.sizesEntityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'sizesEntityKey' cannot be null`);
                    this.sizesEntityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sizesEntityKey'`);
                }
        
                return true;
            case 'layouts':
                // Implementation: axon/readProperty/dictionary
                if (propertyHeader.type === 'dictionary') {
                    const dictionaryHeader = reader.readDictionaryHeader();
                
                    this.layouts = {};
                
                    for (let i = 0; i < dictionaryHeader.recordCount; i++) {
                        let key: string = <any>undefined;
                        let value: LayoutEntry = <any>undefined;
                
                        const dictionaryItemHeader = reader.readDictionaryItemHeader();
                
                        // Key Implementation: string
                        if (dictionaryItemHeader.keyType === 'string') {
                            key = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.keyType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'layouts' cannot be null`);
                            key = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.keyType}' for property 'layouts'`);
                        }
                
                        // Value Implementation: model
                        if (dictionaryItemHeader.valueType === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === LayoutEntry.modelName) {
                                value = new LayoutEntry();
                                value.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${LayoutEntry.modelName}'`);
                            }
                        }
                        else if (dictionaryItemHeader.valueType === 'null') {
                            throw new Error(`Value for property 'layouts' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.valueType}' for property 'layouts'`);
                        }
                
                        this.layouts[key] = value;
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'layouts' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layouts'`);
                }
        
                return true;
            case 'layoutName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.layoutName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'layoutName' cannot be null`);
                    this.layoutName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layoutName'`);
                }
        
                return true;
            case 'sizesGridName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.sizesGridName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'sizesGridName' cannot be null`);
                    this.sizesGridName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sizesGridName'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityKey
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sizesEntityKey
        if (this.isSizesEntityKeyDefined && this.sizesEntityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.sizesEntityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.sizesEntityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layouts
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/dictionary
            if (this.layouts === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(_.keys(this.layouts).length);
            
                for (let key in this.layouts) {
                    // Key Implementation: string
                    if (key === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(key);
                    }
            
                    // Value Implementation: model
                    if (this.layouts[key] === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        this.layouts[key].slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layoutName
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.layoutName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.layoutName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sizesGridName
        if (this.isSizesGridNameDefined && this.sizesGridName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.sizesGridName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.sizesGridName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityKey = <any>null;
                    break;
                case 1:
                    this.entityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // sizesEntityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.sizesEntityKey = <any>null;
                    break;
                case 1:
                    this.sizesEntityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layouts
        if (reader.readBooleanValue())
        {
            // Implementation: dictionary
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null dictionaries');
                    break;
                case 1:
                    let key: string;
                    let value: LayoutEntry;
            
                    this.layouts = {};
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Key Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                key = <any>null;
                                break;
                            case 1:
                                key = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        // Value Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                value = new LayoutEntry();
                                value.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.layouts[key] = value;
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layoutName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.layoutName = <any>null;
                    break;
                case 1:
                    this.layoutName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // sizesGridName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.sizesGridName = <any>null;
                    break;
                case 1:
                    this.sizesGridName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): SelectionContext {
        // Method Implementation: simple
        const schema = SelectionContextZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SelectionContextZodSchemaShape): SelectionContext {
        // Method Implementation: parseZodSchema
        return new SelectionContext({
            entityKey: schema.entityKey,
            sizesEntityKey: schema.sizesEntityKey,
            layouts: schema.layouts,
            layoutName: schema.layoutName,
            sizesGridName: schema.sizesGridName
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityKey: this.isEntityKeyDefined ? ((this.entityKey === null ? undefined : this.entityKey)) : undefined,
            sizesEntityKey: this.isSizesEntityKeyDefined ? ((this.sizesEntityKey === null ? undefined : this.sizesEntityKey)) : undefined,
            layouts: this.isLayoutsDefined ? (this.layouts) : undefined,
            layoutName: this.isLayoutNameDefined ? ((this.layoutName === null ? undefined : this.layoutName)) : undefined,
            sizesGridName: this.isSizesGridNameDefined ? ((this.sizesGridName === null ? undefined : this.sizesGridName)) : undefined,
        };
    }

    public clone(): SelectionContext {
        // Method Implementation: clone
        return new SelectionContext({
            entityKey: this.isEntityKeyDefined ? this.entityKey : undefined,
            sizesEntityKey: this.isSizesEntityKeyDefined ? this.sizesEntityKey : undefined,
            layouts: this.isLayoutsDefined ? this.layouts : undefined,
            layoutName: this.isLayoutNameDefined ? this.layoutName : undefined,
            sizesGridName: this.isSizesGridNameDefined ? this.sizesGridName : undefined
        });
    }
}

export class SelectionInitializationParams implements ISelectionInitializationParams {
    public static readonly modelName: string = 'SelectionInitializationParams';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'application', 'entityKey', 'defaults' ];

    public get modelName(): string {
        // Method Implementation: simple
        return SelectionInitializationParams.modelName;
    }

    private _application: string;

    private _entityKey: string;

    private _defaults: Common.QuestionDefault[];

    public get application(): string {
        // Method Implementation: simple
        return this._application;
    }

    public get entityKey(): string {
        // Method Implementation: simple
        return this._entityKey;
    }

    public get defaults(): Common.QuestionDefault[] {
        // Method Implementation: simple
        return this._defaults;
    }

    public set application(value: string) {
        // Method Implementation: simple
        this._application = value;
        this._isApplicationDefined = true;
    }

    public set entityKey(value: string) {
        // Method Implementation: simple
        this._entityKey = value;
        this._isEntityKeyDefined = true;
    }

    public set defaults(value: Common.QuestionDefault[]) {
        // Method Implementation: simple
        this._defaults = value;
        this._isDefaultsDefined = true;
    }

    private _isApplicationDefined: boolean;

    private _isEntityKeyDefined: boolean;

    private _isDefaultsDefined: boolean;

    public get isApplicationDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicationDefined;
    }

    public get isEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityKeyDefined;
    }

    public get isDefaultsDefined(): boolean {
        // Method Implementation: simple
        return this._isDefaultsDefined;
    }

    public constructor();
    public constructor(source: ISelectionInitializationParams);
    public constructor(source: Partial<Pick<SelectionInitializationParams, 'application' | 'entityKey' | 'defaults'>>);
    public constructor(source?: ISelectionInitializationParams | Partial<Pick<SelectionInitializationParams, 'application' | 'entityKey' | 'defaults'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSelectionInitializationParamsModel(source)) {
                if (source.isApplicationDefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.isEntityKeyDefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.isDefaultsDefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
            else {
                if (source.application !== undefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.entityKey !== undefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.defaults !== undefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
        }
        else {
            this._application = '';
            this._isApplicationDefined = false;
        
            this._entityKey = '';
            this._isEntityKeyDefined = false;
        
            this._defaults = [];
            this._isDefaultsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return SelectionInitializationParams.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isApplicationDefined && this.application !== undefined)
            definedProperties.push('application');
        if (this.isEntityKeyDefined && this.entityKey !== undefined)
            definedProperties.push('entityKey');
        if (this.isDefaultsDefined && this.defaults !== undefined)
            definedProperties.push('defaults');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'application':
                return this.application;
            case 'entityKey':
                return this.entityKey;
            case 'defaults':
                return this.defaults;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isApplicationDefined && this.application !== undefined) {
            if (this.application !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.application);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'null'));
            }
        }
        
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            if (this.entityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'null'));
            }
        }
        
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            if (this.defaults !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.defaults.length));
                for (let i = 0; i < this.defaults.length; i++) {
                    const item = this.defaults[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'application':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.application = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'application' cannot be null`);
                    this.application = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'application'`);
                }
        
                return true;
            case 'entityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityKey' cannot be null`);
                    this.entityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityKey'`);
                }
        
                return true;
            case 'defaults':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.defaults = [];
                
                    let item: Common.QuestionDefault = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Common.QuestionDefault.modelName) {
                                item = new Common.QuestionDefault();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Common.QuestionDefault.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'defaults' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'defaults'`);
                        }
                
                        this.defaults.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'defaults' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'defaults'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // application
        if (this.isApplicationDefined && this.application !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.application === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.application);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // entityKey
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // defaults
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.defaults === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.defaults.length);
            
                for (const item of this.defaults) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // application
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.application = <any>null;
                    break;
                case 1:
                    this.application = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // entityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityKey = <any>null;
                    break;
                case 1:
                    this.entityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // defaults
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Common.QuestionDefault;
            
                    this.defaults = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Common.QuestionDefault();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.defaults.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): SelectionInitializationParams {
        // Method Implementation: simple
        const schema = SelectionInitializationParamsZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SelectionInitializationParamsZodSchemaShape): SelectionInitializationParams {
        // Method Implementation: parseZodSchema
        return new SelectionInitializationParams({
            application: schema.application,
            entityKey: schema.entityKey,
            defaults: schema.defaults?.map(i => Common.QuestionDefault.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            application: this.isApplicationDefined ? ((this.application === null ? undefined : this.application)) : undefined,
            entityKey: this.isEntityKeyDefined ? ((this.entityKey === null ? undefined : this.entityKey)) : undefined,
            defaults: this.isDefaultsDefined ? (this.defaults?.map(item => item)) : undefined,
        };
    }

    public clone(): SelectionInitializationParams {
        // Method Implementation: clone
        return new SelectionInitializationParams({
            application: this.isApplicationDefined ? this.application : undefined,
            entityKey: this.isEntityKeyDefined ? this.entityKey : undefined,
            defaults: this.isDefaultsDefined ? this.defaults?.map(i => i.clone()) : undefined
        });
    }
}

export class UpdateRequest implements IUpdateRequest {
    public static readonly modelName: string = 'UpdateRequest';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'questions', 'addedSizes', 'removedSizes' ];

    public get modelName(): string {
        // Method Implementation: simple
        return UpdateRequest.modelName;
    }

    private _questions: Configuration.EntityStateQuestion[];

    private _addedSizes: UpdateRequestSize[];

    private _removedSizes: string[];

    public get questions(): Configuration.EntityStateQuestion[] {
        // Method Implementation: simple
        return this._questions;
    }

    public get addedSizes(): UpdateRequestSize[] {
        // Method Implementation: simple
        return this._addedSizes;
    }

    public get removedSizes(): string[] {
        // Method Implementation: simple
        return this._removedSizes;
    }

    public set questions(value: Configuration.EntityStateQuestion[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    public set addedSizes(value: UpdateRequestSize[]) {
        // Method Implementation: simple
        this._addedSizes = value;
        this._isAddedSizesDefined = true;
    }

    public set removedSizes(value: string[]) {
        // Method Implementation: simple
        this._removedSizes = value;
        this._isRemovedSizesDefined = true;
    }

    private _isQuestionsDefined: boolean;

    private _isAddedSizesDefined: boolean;

    private _isRemovedSizesDefined: boolean;

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public get isAddedSizesDefined(): boolean {
        // Method Implementation: simple
        return this._isAddedSizesDefined;
    }

    public get isRemovedSizesDefined(): boolean {
        // Method Implementation: simple
        return this._isRemovedSizesDefined;
    }

    public constructor();
    public constructor(source: IUpdateRequest);
    public constructor(source: Partial<Pick<UpdateRequest, 'questions' | 'addedSizes' | 'removedSizes'>>);
    public constructor(source?: IUpdateRequest | Partial<Pick<UpdateRequest, 'questions' | 'addedSizes' | 'removedSizes'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isUpdateRequestModel(source)) {
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
                
                if (source.isAddedSizesDefined) {
                    this._addedSizes = source.addedSizes;
                    this._isAddedSizesDefined = true;
                }
                else {
                    this._addedSizes = [];
                    this._isAddedSizesDefined = true;
                }
                
                if (source.isRemovedSizesDefined) {
                    this._removedSizes = source.removedSizes;
                    this._isRemovedSizesDefined = true;
                }
                else {
                    this._removedSizes = [];
                    this._isRemovedSizesDefined = true;
                }
            }
            else {
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
                
                if (source.addedSizes !== undefined) {
                    this._addedSizes = source.addedSizes;
                    this._isAddedSizesDefined = true;
                }
                else {
                    this._addedSizes = [];
                    this._isAddedSizesDefined = true;
                }
                
                if (source.removedSizes !== undefined) {
                    this._removedSizes = source.removedSizes;
                    this._isRemovedSizesDefined = true;
                }
                else {
                    this._removedSizes = [];
                    this._isRemovedSizesDefined = true;
                }
            }
        }
        else {
            this._questions = [];
            this._isQuestionsDefined = true;
        
            this._addedSizes = [];
            this._isAddedSizesDefined = true;
        
            this._removedSizes = [];
            this._isRemovedSizesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return UpdateRequest.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        if (this.isAddedSizesDefined && this.addedSizes !== undefined)
            definedProperties.push('addedSizes');
        if (this.isRemovedSizesDefined && this.removedSizes !== undefined)
            definedProperties.push('removedSizes');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'questions':
                return this.questions;
            case 'addedSizes':
                return this.addedSizes;
            case 'removedSizes':
                return this.removedSizes;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
        
        if (this.isAddedSizesDefined && this.addedSizes !== undefined) {
            if (this.addedSizes !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('addedSizes', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.addedSizes.length));
                for (let i = 0; i < this.addedSizes.length; i++) {
                    const item = this.addedSizes[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('addedSizes', 'null'));
            }
        }
        
        if (this.isRemovedSizesDefined && this.removedSizes !== undefined) {
            if (this.removedSizes !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('removedSizes', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.removedSizes.length));
                for (let i = 0; i < this.removedSizes.length; i++) {
                    const item = this.removedSizes[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('removedSizes', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: Configuration.EntityStateQuestion = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Configuration.EntityStateQuestion.modelName) {
                                item = new Configuration.EntityStateQuestion();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Configuration.EntityStateQuestion.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            case 'addedSizes':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.addedSizes = [];
                
                    let item: UpdateRequestSize = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === UpdateRequestSize.modelName) {
                                item = new UpdateRequestSize();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${UpdateRequestSize.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'addedSizes' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'addedSizes'`);
                        }
                
                        this.addedSizes.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'addedSizes' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'addedSizes'`);
                }
        
                return true;
            case 'removedSizes':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.removedSizes = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'removedSizes' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'removedSizes'`);
                        }
                
                        this.removedSizes.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'removedSizes' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'removedSizes'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // addedSizes
        if (this.isAddedSizesDefined && this.addedSizes !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.addedSizes === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.addedSizes.length);
            
                for (const item of this.addedSizes) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // removedSizes
        if (this.isRemovedSizesDefined && this.removedSizes !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.removedSizes === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.removedSizes.length);
            
                for (const item of this.removedSizes) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Configuration.EntityStateQuestion;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Configuration.EntityStateQuestion();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // addedSizes
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: UpdateRequestSize;
            
                    this.addedSizes = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new UpdateRequestSize();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.addedSizes.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // removedSizes
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.removedSizes = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.removedSizes.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): UpdateRequest {
        // Method Implementation: simple
        const schema = UpdateRequestZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: UpdateRequestZodSchemaShape): UpdateRequest {
        // Method Implementation: parseZodSchema
        return new UpdateRequest({
            questions: schema.questions?.map(i => Configuration.EntityStateQuestion.parseZodSchema(i)),
            addedSizes: schema.addedSizes?.map(i => UpdateRequestSize.parseZodSchema(i)),
            removedSizes: schema.removedSizes?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
            addedSizes: this.isAddedSizesDefined ? (this.addedSizes?.map(item => item)) : undefined,
            removedSizes: this.isRemovedSizesDefined ? (this.removedSizes?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): UpdateRequest {
        // Method Implementation: clone
        return new UpdateRequest({
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined,
            addedSizes: this.isAddedSizesDefined ? this.addedSizes?.map(i => i.clone()) : undefined,
            removedSizes: this.isRemovedSizesDefined ? this.removedSizes?.map(i => i) : undefined
        });
    }
}

export class UpdateRequestSize implements IUpdateRequestSize {
    public static readonly modelName: string = 'UpdateRequestSize';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'defaults' ];

    public get modelName(): string {
        // Method Implementation: simple
        return UpdateRequestSize.modelName;
    }

    private _defaults: Common.QuestionDefault[];

    public get defaults(): Common.QuestionDefault[] {
        // Method Implementation: simple
        return this._defaults;
    }

    public set defaults(value: Common.QuestionDefault[]) {
        // Method Implementation: simple
        this._defaults = value;
        this._isDefaultsDefined = true;
    }

    private _isDefaultsDefined: boolean;

    public get isDefaultsDefined(): boolean {
        // Method Implementation: simple
        return this._isDefaultsDefined;
    }

    public constructor();
    public constructor(source: IUpdateRequestSize);
    public constructor(source: Partial<Pick<UpdateRequestSize, 'defaults'>>);
    public constructor(source?: IUpdateRequestSize | Partial<Pick<UpdateRequestSize, 'defaults'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isUpdateRequestSizeModel(source)) {
                if (source.isDefaultsDefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
            else {
                if (source.defaults !== undefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
        }
        else {
            this._defaults = [];
            this._isDefaultsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return UpdateRequestSize.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isDefaultsDefined && this.defaults !== undefined)
            definedProperties.push('defaults');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'defaults':
                return this.defaults;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            if (this.defaults !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.defaults.length));
                for (let i = 0; i < this.defaults.length; i++) {
                    const item = this.defaults[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'defaults':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.defaults = [];
                
                    let item: Common.QuestionDefault = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Common.QuestionDefault.modelName) {
                                item = new Common.QuestionDefault();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Common.QuestionDefault.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'defaults' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'defaults'`);
                        }
                
                        this.defaults.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'defaults' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'defaults'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // defaults
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.defaults === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.defaults.length);
            
                for (const item of this.defaults) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // defaults
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Common.QuestionDefault;
            
                    this.defaults = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Common.QuestionDefault();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.defaults.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): UpdateRequestSize {
        // Method Implementation: simple
        const schema = UpdateRequestSizeZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: UpdateRequestSizeZodSchemaShape): UpdateRequestSize {
        // Method Implementation: parseZodSchema
        return new UpdateRequestSize({
            defaults: schema.defaults?.map(i => Common.QuestionDefault.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            defaults: this.isDefaultsDefined ? (this.defaults?.map(item => item)) : undefined,
        };
    }

    public clone(): UpdateRequestSize {
        // Method Implementation: clone
        return new UpdateRequestSize({
            defaults: this.isDefaultsDefined ? this.defaults?.map(i => i.clone()) : undefined
        });
    }
}

export interface ISelectionService {
    init(context: SelectionContext, initializationParams: SelectionInitializationParams): Promise<void>;

    select(context: SelectionContext): Promise<void>;

    update(context: SelectionContext, updates: Configuration.EntityStateQuestion[]): Promise<void>;

    cleanup(context: SelectionContext): Promise<void>;
}

export interface ISizingService {
    init(context: SizingContext, initializationParams: SizingInitializationParams): Promise<void>;

    addSize(context: SizingContext): Promise<void>;

    addSize(context: SizingContext, defaults: Common.QuestionDefault[]): Promise<void>;

    removeSize(context: SizingContext, sizeId: string): Promise<void>;

    addPerformanceSizes(context: SizingContext, performanceSelectorEntityKey: string, performanceSizeIds: string[]): Promise<void>;

    addPerformanceSizes(context: SizingContext, performanceSelectorEntityKey: string, performanceSizeIds: string[], defaults: Common.QuestionDefault[]): Promise<void>;

    applyPerformanceSize(context: SizingContext, sizeId: string, performanceSelectorEntityKey: string, performanceSizeId: string): Promise<void>;

    update(context: SizingContext, updates: UpdateRequest): Promise<void>;

    cleanup(context: SizingContext): Promise<void>;
}

export class SelectionServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public init(context: SelectionContext, initializationParams: SelectionInitializationParams): Promise<void>;
    public init(actionContext: Wellspring.ActionContext, context: SelectionContext, initializationParams: SelectionInitializationParams): Promise<void>;
    public async init(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext), 
            // {"template":"model","className":"SelectionInitializationParams"}
            Zod.instanceof(SelectionInitializationParams)
        ]).check(args)) {
            const [ context, initializationParams ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.init');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('init', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("9490abe14180c02faa5f52955c54492268796e4142dabb2e7dcc6298b27ead78");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (initializationParams && initializationParams !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (initializationParams === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            initializationParams.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].init`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                initializationParams: JSON.parse(JSON.stringify(initializationParams))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/init" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        initializationParams: JSON.parse(JSON.stringify(initializationParams))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        initializationParams: JSON.parse(JSON.stringify(initializationParams))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public select(context: SelectionContext): Promise<void>;
    public select(actionContext: Wellspring.ActionContext, context: SelectionContext): Promise<void>;
    public async select(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.select');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('select', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("518fe934f79fb11de98b8358f3cd316253bb58b050145c56c4629264d4392609");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].select`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/select" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public update(context: SelectionContext, updates: Configuration.EntityStateQuestion[]): Promise<void>;
    public update(actionContext: Wellspring.ActionContext, context: SelectionContext, updates: Configuration.EntityStateQuestion[]): Promise<void>;
    public async update(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext), 
            // {"template":"array","itemImplementation":{"template":"model","className":"Configuration.EntityStateQuestion"}}
            Zod.array(Zod.instanceof(Configuration.EntityStateQuestion))
        ]).check(args)) {
            const [ context, updates ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.update');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('update', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("9de10531a5b83ca7e734467f3c76b0b874a165c089a728cdf972a8d9deb8f18b");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (updates && updates !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (updates === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(updates.length);
                        
                            for (const item of updates) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].update`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                updates: JSON.parse(JSON.stringify(updates))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/update" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public cleanup(context: SelectionContext): Promise<void>;
    public cleanup(actionContext: Wellspring.ActionContext, context: SelectionContext): Promise<void>;
    public async cleanup(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.cleanup');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('cleanup', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("2813206e9ea54cd05460e31528326c9b29d80d1194bd7dad43c178d9ee0eb2a5");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].cleanup`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/cleanup" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].cleanup`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].cleanup`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class SizingServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public init(context: SizingContext, initializationParams: SizingInitializationParams): Promise<void>;
    public init(actionContext: Wellspring.ActionContext, context: SizingContext, initializationParams: SizingInitializationParams): Promise<void>;
    public async init(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext), 
            // {"template":"model","className":"SizingInitializationParams"}
            Zod.instanceof(SizingInitializationParams)
        ]).check(args)) {
            const [ context, initializationParams ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.init');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('init', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("cc478da3eb6ef149f423b055a28b08b292b7188f52dcb84049075ca32cf0f854");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (initializationParams && initializationParams !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (initializationParams === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            initializationParams.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].init`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                initializationParams: JSON.parse(JSON.stringify(initializationParams))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/init" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        initializationParams: JSON.parse(JSON.stringify(initializationParams))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        initializationParams: JSON.parse(JSON.stringify(initializationParams))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public addSize(context: SizingContext): Promise<void>;
    public addSize(actionContext: Wellspring.ActionContext, context: SizingContext): Promise<void>;
    public addSize(context: SizingContext, defaults: Common.QuestionDefault[]): Promise<void>;
    public addSize(actionContext: Wellspring.ActionContext, context: SizingContext, defaults: Common.QuestionDefault[]): Promise<void>;
    public async addSize(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.addSize');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('addSize', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("6d4c9163ab6c7e4b55fb774c93d1dba636b9bfa5eca67165da0a4271321709e1");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addSize`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/addSize" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].addSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext), 
            // {"template":"array","itemImplementation":{"template":"model","className":"Common.QuestionDefault"}}
            Zod.array(Zod.instanceof(Common.QuestionDefault))
        ]).check(args)) {
            const [ context, defaults ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.addSize');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('addSize', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("c03e3e4307f70c62a5aca9afd10f49144db9412fb16feda2d6f1556385fffe65");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (defaults && defaults !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (defaults === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(defaults.length);
                        
                            for (const item of defaults) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addSize`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                defaults: JSON.parse(JSON.stringify(defaults))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/addSize" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].addSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public removeSize(context: SizingContext, sizeId: string): Promise<void>;
    public removeSize(actionContext: Wellspring.ActionContext, context: SizingContext, sizeId: string): Promise<void>;
    public async removeSize(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ context, sizeId ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.removeSize');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('removeSize', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("6bb6f7182ffa003adf52bdc9ffbc4190fd62691b07638542ab7ee3f4980b6129");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (sizeId && sizeId !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (sizeId === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(sizeId);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].removeSize`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                sizeId: JSON.parse(JSON.stringify(sizeId))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/removeSize" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].removeSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        sizeId: JSON.parse(JSON.stringify(sizeId))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].removeSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        sizeId: JSON.parse(JSON.stringify(sizeId))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public addPerformanceSizes(context: SizingContext, performanceSelectorEntityKey: string, performanceSizeIds: string[]): Promise<void>;
    public addPerformanceSizes(actionContext: Wellspring.ActionContext, context: SizingContext, performanceSelectorEntityKey: string, performanceSizeIds: string[]): Promise<void>;
    public addPerformanceSizes(context: SizingContext, performanceSelectorEntityKey: string, performanceSizeIds: string[], defaults: Common.QuestionDefault[]): Promise<void>;
    public addPerformanceSizes(actionContext: Wellspring.ActionContext, context: SizingContext, performanceSelectorEntityKey: string, performanceSizeIds: string[], defaults: Common.QuestionDefault[]): Promise<void>;
    public async addPerformanceSizes(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"array","itemImplementation":{"template":"string"}}
            Zod.array(Zod.string())
        ]).check(args)) {
            const [ context, performanceSelectorEntityKey, performanceSizeIds ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.addPerformanceSizes');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('addPerformanceSizes', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(4);
                    writer.writeStringValue("5006aa5d6f12bd6578eb67c0bc60d7687760cb624fa9bb4007866e764991fdb9");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (performanceSelectorEntityKey && performanceSelectorEntityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (performanceSelectorEntityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(performanceSelectorEntityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (performanceSizeIds && performanceSizeIds !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (performanceSizeIds === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(performanceSizeIds.length);
                        
                            for (const item of performanceSizeIds) {
                                // Item Implementation: string
                                if (item === null)
                                {
                                    writer.writeByteValue(0);
                                }
                                else
                                {
                                    writer.writeByteValue(1);
                                    writer.writeStringValue(item);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addPerformanceSizes`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                                performanceSizeIds: JSON.parse(JSON.stringify(performanceSizeIds))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/addPerformanceSizes" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].addPerformanceSizes`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                        performanceSizeIds: JSON.parse(JSON.stringify(performanceSizeIds))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addPerformanceSizes`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                        performanceSizeIds: JSON.parse(JSON.stringify(performanceSizeIds))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"array","itemImplementation":{"template":"string"}}
            Zod.array(Zod.string()), 
            // {"template":"array","itemImplementation":{"template":"model","className":"Common.QuestionDefault"}}
            Zod.array(Zod.instanceof(Common.QuestionDefault))
        ]).check(args)) {
            const [ context, performanceSelectorEntityKey, performanceSizeIds, defaults ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.addPerformanceSizes');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('addPerformanceSizes', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(5);
                    writer.writeStringValue("beedb5a6b0736910bc0ead7b34172b3f9854f98468f28d26d703b4b7daf67306");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (performanceSelectorEntityKey && performanceSelectorEntityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (performanceSelectorEntityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(performanceSelectorEntityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (performanceSizeIds && performanceSizeIds !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (performanceSizeIds === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(performanceSizeIds.length);
                        
                            for (const item of performanceSizeIds) {
                                // Item Implementation: string
                                if (item === null)
                                {
                                    writer.writeByteValue(0);
                                }
                                else
                                {
                                    writer.writeByteValue(1);
                                    writer.writeStringValue(item);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (defaults && defaults !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (defaults === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(defaults.length);
                        
                            for (const item of defaults) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addPerformanceSizes`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                                performanceSizeIds: JSON.parse(JSON.stringify(performanceSizeIds)),
                                defaults: JSON.parse(JSON.stringify(defaults))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/addPerformanceSizes" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].addPerformanceSizes`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                        performanceSizeIds: JSON.parse(JSON.stringify(performanceSizeIds)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].addPerformanceSizes`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                        performanceSizeIds: JSON.parse(JSON.stringify(performanceSizeIds)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public applyPerformanceSize(context: SizingContext, sizeId: string, performanceSelectorEntityKey: string, performanceSizeId: string): Promise<void>;
    public applyPerformanceSize(actionContext: Wellspring.ActionContext, context: SizingContext, sizeId: string, performanceSelectorEntityKey: string, performanceSizeId: string): Promise<void>;
    public async applyPerformanceSize(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ context, sizeId, performanceSelectorEntityKey, performanceSizeId ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.applyPerformanceSize');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('applyPerformanceSize', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(6);
                    writer.writeStringValue("2ccf0bcc56c0be9067a770d3e95e3c1f4f4a6b01af7bd81f9f9754f0cd80539d");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (sizeId && sizeId !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (sizeId === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(sizeId);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (performanceSelectorEntityKey && performanceSelectorEntityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (performanceSelectorEntityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(performanceSelectorEntityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (performanceSizeId && performanceSizeId !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (performanceSizeId === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(performanceSizeId);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].applyPerformanceSize`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                sizeId: JSON.parse(JSON.stringify(sizeId)),
                                performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                                performanceSizeId: JSON.parse(JSON.stringify(performanceSizeId))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/applyPerformanceSize" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].applyPerformanceSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        sizeId: JSON.parse(JSON.stringify(sizeId)),
                        performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                        performanceSizeId: JSON.parse(JSON.stringify(performanceSizeId))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].applyPerformanceSize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        sizeId: JSON.parse(JSON.stringify(sizeId)),
                        performanceSelectorEntityKey: JSON.parse(JSON.stringify(performanceSelectorEntityKey)),
                        performanceSizeId: JSON.parse(JSON.stringify(performanceSizeId))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public update(context: SizingContext, updates: UpdateRequest): Promise<void>;
    public update(actionContext: Wellspring.ActionContext, context: SizingContext, updates: UpdateRequest): Promise<void>;
    public async update(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext), 
            // {"template":"model","className":"UpdateRequest"}
            Zod.instanceof(UpdateRequest)
        ]).check(args)) {
            const [ context, updates ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.update');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('update', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(7);
                    writer.writeStringValue("89f9e8fc63529399a7a7945b940727cfb90f1b0a2a731accad81f607952a9d8d");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (updates && updates !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (updates === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            updates.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].update`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                updates: JSON.parse(JSON.stringify(updates))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/update" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public cleanup(context: SizingContext): Promise<void>;
    public cleanup(actionContext: Wellspring.ActionContext, context: SizingContext): Promise<void>;
    public async cleanup(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SizingContext"}
            Zod.instanceof(SizingContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('sizing.cleanup');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('cleanup', 'utf8'));
                requestMetadata.add('service', Buffer.from('sizing', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(8);
                    writer.writeStringValue("fe6f22eaa863417bd7c4fe5f4cd627fd8834f99eedb9879f3e0ccf7269d794c4");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].cleanup`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "sizing/cleanup" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `sizing[${this.identifier}].cleanup`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `sizing[${this.identifier}].cleanup`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'LayoutEntry':
                return new LayoutEntry();
            case 'SizingContext':
                return new SizingContext();
            case 'SizingInitializationParams':
                return new SizingInitializationParams();
            case 'SizingSize':
                return new SizingSize();
            case 'SelectionContext':
                return new SelectionContext();
            case 'SelectionInitializationParams':
                return new SelectionInitializationParams();
            case 'UpdateRequest':
                return new UpdateRequest();
            case 'UpdateRequestSize':
                return new UpdateRequestSize();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'LayoutEntry':
                return new LayoutEntry(source);
            case 'SizingContext':
                return new SizingContext(source);
            case 'SizingInitializationParams':
                return new SizingInitializationParams(source);
            case 'SizingSize':
                return new SizingSize(source);
            case 'SelectionContext':
                return new SelectionContext(source);
            case 'SelectionInitializationParams':
                return new SelectionInitializationParams(source);
            case 'UpdateRequest':
                return new UpdateRequest(source);
            case 'UpdateRequestSize':
                return new UpdateRequestSize(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'LayoutEntry':
                return LayoutEntry.parse(source);
            case 'SizingContext':
                return SizingContext.parse(source);
            case 'SizingInitializationParams':
                return SizingInitializationParams.parse(source);
            case 'SizingSize':
                return SizingSize.parse(source);
            case 'SelectionContext':
                return SelectionContext.parse(source);
            case 'SelectionInitializationParams':
                return SelectionInitializationParams.parse(source);
            case 'UpdateRequest':
                return UpdateRequest.parse(source);
            case 'UpdateRequestSize':
                return UpdateRequestSize.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
